import Service                                                                                             from '@/api/service';
import i18n                                                                                                from '@/lang';
import { centToEuro, euroToCent, getKeyValue, getKeyValueArray, getKeyValueProfile, getKeyValueTrad }      from '@/composables/formatData';
import { getIsVefaSaleType, getSaleType }                                                                  from '@/composables/saleType';
import { getKeyValueActorsArray }                                                                          from '@/composables/splitAgents';
import { hasProformaUpdatedManually, useGetCalculationBasis, useGetCommissionData, useGetTotalCommission } from '@/composables/commission';
import { useIsMandateExpireInLessThan3Months }                                                             from '@/composables/mandate';
import { PermissionManager }                                                                               from '@/utils/permissions/permissionManager.js';
import dayjs                                                                                               from 'dayjs';

export const FOLDER_STATUS_TODO = 'todo';
export const FOLDER_STATUS_CDV_IN_PROGESS = 'cdv_in_progress';
export const FOLDER_STATUS_COOLING_OFF_PERIOD = 'cooling_off_period';
export const FOLDER_STATUS_CDV_CLAUSES_BEING_FULFILLED = 'cdv_clauses_being_fulfilled';
export const FOLDER_STATUS_PREPARATION_FOR_ACTE = 'preparation_for_acte';
export const FOLDER_STATUS_ACTE_DATE_CONFIRMED = 'acte_date_confirmed';
export const FOLDER_STATUS_ADV_SIGNED = 'adv_signed';
export const FOLDER_STATUS_CALCULATING_COMMISSIONS = 'calculating_commissions';
export const FOLDER_STATUS_COMPLETED = 'completed';
export const FOLDER_STATUS_CANCELLED = 'cancelled';

export function useFolderStatusList() {

  const messages = i18n.global.messages[i18n.global.locale];
  return [
    {
      key   : FOLDER_STATUS_TODO,
      value : messages.folders.status.todo,
      color : 'grey-300'
    }, {
      key   : FOLDER_STATUS_CDV_IN_PROGESS,
      value : messages.folders.status.cdv_in_progress,
      color : 'yellow'
    }, {
      key   : FOLDER_STATUS_COOLING_OFF_PERIOD,
      value : messages.folders.status.cooling_off_period,
      color : 'yellow'
    }, {
      key   : FOLDER_STATUS_CDV_CLAUSES_BEING_FULFILLED,
      value : messages.folders.status.cdv_clauses_being_fulfilled,
      color : 'yellow'
    }, {
      key   : FOLDER_STATUS_PREPARATION_FOR_ACTE,
      value : messages.folders.status.preparation_for_acte,
      color : 'yellow'
    }, {
      key   : FOLDER_STATUS_ACTE_DATE_CONFIRMED,
      value : messages.folders.status.acte_date_confirmed,
      color : 'blue-300'
    }, {
      key   : FOLDER_STATUS_ADV_SIGNED,
      value : messages.folders.status.adv_signed,
      color : 'blue-300'
    }, {
      key   : FOLDER_STATUS_CALCULATING_COMMISSIONS,
      value : messages.folders.status.calculating_commissions,
      color : 'blue-300'
    }, {
      key   : FOLDER_STATUS_COMPLETED,
      value : messages.folders.status.completed,
      color : 'green'
    }, {
      key   : FOLDER_STATUS_CANCELLED,
      value : messages.folders.status.cancelled,
      color : 'red'
    }
  ];
}

export async function useFormatFolderTab(folder, hasDispute) {

  const actors = await Service.getActorsWithoutPagination(folder.tenantId, {
    searchParams : { 'dossierId:eq' : folder.id }
  });

  const formatFolder = {
    id                     : folder.id,
    classeurId             : folder.classeurId,
    name                   : folder.name,
    status                 : getKeyValueTrad('folders.status', folder.status),
    validationByHeadOffice : getKeyValueTrad('folders.validationByHeadOffice', folder.validationByHeadOffice),
    actSignatureDate       : folder.actSignatureDate ? new Date(folder.actSignatureDate) : '',
    price                  : centToEuro(folder.price),
    hasDispute             : hasDispute,
    cancellingReason       : folder.cancellingReason,
    rollings               : {
      updated   : folder.rollings.updated ? folder.rollings.updated : false,
      updatedAt : folder.rollings.updatedAt ? new Date(folder.rollings.updatedAt) : ''
    },
    actors
  };

  if (PermissionManager.getPermissionForElement(PermissionManager.permissionElement.dossiers.informations.showHideFolder)) {

    formatFolder.hidden = folder.hidden;
  }

  return formatFolder;
}

export async function useFormatFolderLineHeader(tenantId, folder, dispute) {

  const formatFolder = {
    actSignatureDate     : folder.actSignatureDate ? new Date(folder.actSignatureDate) : '',
    assignedTo           : folder.assignedTo ? await getKeyValue(tenantId, folder.assignedTo) : '',
    classeurId           : folder.classeurId,
    createdAt            : folder.createdAt,
    dispute              : dispute,
    expandable           : true,
    fees                 : centToEuro(folder.fees),
    id                   : folder.id,
    isThereFolderContent : false,
    cancellingReason     : folder.cancellingReason,
    name                 : folder.name,
    price                : centToEuro(folder.price),
    rollings             : {
      updated   : folder.rollings.updated ? folder.rollings.updated : false,
      updatedAt : folder.rollings.updatedAt ? new Date(folder.rollings.updatedAt) : ''
    },
    status               : getKeyValueTrad('folders.status', folder.status),
    updatedAt            : folder.updatedAt
  };

  if (PermissionManager.getPermissionForElement(PermissionManager.permissionElement.dossiers.informations.showHideFolder)) {

    formatFolder.hidden = folder.hidden;
  }

  return formatFolder;
}

export async function useFormatFolderLineContent(tenantId, folder) {

  const formattedFolder = {
    buyers               : await getKeyValueClientsWithNotaryClerk(tenantId, folder.buyers),
    cdvType              : folder.cdvType ? getKeyValueTrad('folders.cdvTypes', folder.cdvType) : '',
    clientAgents         : await getKeyValueActorsArray(tenantId, folder.id, 'client'),
    commune              : folder.address && Object.keys(folder.address).length > 0 ? `${ folder.address.town } (${ folder.address.postcode })` : '',
    exclusive            : folder.exclusive,
    isThereFolderContent : true,
    loanStatus           : folder.loanStatus ? getKeyValueTrad('folders.loanStatus', folder.loanStatus) : '',
    mandateAgents        : await getKeyValueActorsArray(tenantId, folder.id, 'mandate'),
    primaryNotary        : folder.primaryNotary ? await getKeyValue(tenantId, folder.primaryNotary) : '',
    property             : folder.property?.id ? getIdReferenceObject(folder.property) : '',
    saleAgents           : await getKeyValueActorsArray(tenantId, folder.id, 'sale'),
    sellers              : await getKeyValueClientsWithNotaryClerk(tenantId, folder.sellers)
  };

  formattedFolder.agents = [...formattedFolder.mandateAgents, ...formattedFolder.saleAgents, ...formattedFolder.clientAgents];
  formattedFolder.notaries = getDifferentNotaries(formattedFolder.sellers, formattedFolder.buyers, formattedFolder.primaryNotary);

  return formattedFolder;
}

export async function useFormatFolderToCalculate(tenantId, folder) {

  return {
    actSignatureDate                  : folder.actSignatureDate ? new Date(folder.actSignatureDate) : '',
    agents                            : await getKeyValueArray(tenantId, folder.agents),
    classeurId                        : folder.classeurId,
    thirdPartyInvoiceFundsReceiptDate : folder.primaryInvoice?.fundsReceiptDate ? new Date(folder.primaryInvoice.fundsReceiptDate) : '',
    id                                : folder.id,
    name                              : folder.name,
    rollings                          : {
      updated   : folder.rollings.updated ? folder.rollings.updated : false,
      updatedAt : folder.rollings.updatedAt ? new Date(folder.rollings.updatedAt) : ''
    }
  };
}

export function useFormatFolderWaitingForValidation(folder, userId) {

  const actor = folder.actors.find(actor => actor.userId === userId);
  return {
    id                         : folder.id,
    classeurId                 : folder.classeurId,
    name                       : folder.name,
    priceHAI                   : centToEuro(folder.price + folder.fees),
    fees                       : centToEuro(folder.fees),
    validationNotificationDate : actor?.notifications?.dossier_validated_by_head_office?.sentAt || null
  };
}

export function useFormatFolderThirdPartyInvoicesToBeCreated(folder) {

  return {
    id                        : folder.id,
    classeurId                : folder.classeurId,
    name                      : folder.name,
    fees                      : centToEuro(folder.fees),
    actSignatureDate          : folder.actSignatureDate ? new Date(folder.actSignatureDate) : '',
    estimatedActSignatureDate : folder.estimatedActSignatureDate ? new Date(folder.estimatedActSignatureDate) : '',
    rollings                  : {
      updated   : folder.rollings.updated ? folder.rollings.updated : false,
      updatedAt : folder.rollings.updatedAt ? new Date(folder.rollings.updatedAt) : ''
    }
  };
}

function getIdReferenceObject(object) {

  return {
    id        : object.id,
    reference : object.reference ? object.reference : object.id
  };
}

async function isCommissionsCalculated(tenantId, folderId) {

  const commissions = await Service.getCommissions(tenantId, {
    'dossierId:eq' : folderId,
    perPage        : 1
  });

  return commissions.results.length > 0;
}

async function getKeyValueClientsWithNotaryClerk(tenantId, clients) {

  const formattedClients = [];

  for (const client of clients) {

    const notary = client.notaryId ? await getKeyValue(tenantId, client.notaryId) : '';
    const clerk = client.notaryClerkId ? await getKeyValue(tenantId, client.notaryClerkId) : '';

    const clientKeyValue = await getKeyValue(tenantId, client.id);
    const representedByKeyValue = client.representedBy ? await getKeyValue(tenantId, client.representedBy) : null;
    formattedClients.push({
      ...clientKeyValue,
      notary        : notary,
      clerk         : clerk,
      representedBy : representedByKeyValue
    });
  }

  return formattedClients;
}

async function formatPartnerLead(tenantId, partnerLead) {

  let sourceType = partnerLead.source?.type === 'internal' ? 'partner' : '';
  if (partnerLead.source?.type === 'external') {

    sourceType = 'lead';
  }

  const defaultAmount = { value : 0, percent : partnerLead.amount?.percent ?? true };
  if (partnerLead.amount?.value) {
    defaultAmount.value = partnerLead.amount?.percent === false ? centToEuro(partnerLead.amount.value) : partnerLead.amount.value;
  }

  return {
    source     : {
      value : partnerLead.source?.id ? await getKeyValue(tenantId, partnerLead.source.id, sourceType) : '',
      type  : partnerLead.source?.type ? getKeyValueTrad('charges_partners.source.types', partnerLead.source.type) : ''
    },
    amount     : defaultAmount,
    proformaId : partnerLead.proformaId
  };
}

function getDifferentNotaries(sellers, buyers, primaryNotary) {

  const notaries = [
    primaryNotary,
    ...sellers.map(client => client.notary),
    ...buyers.map(client => client.notary)
  ];

  // Remove notary if it's empty or duplicated
  return notaries.filter((item, index) => {

    const findIndex = notaries.findIndex(notary => notary.key === item.key);
    return item.key && findIndex === index;
  });
}

async function areFeesInconsistent(tenantId, fees, primaryInvoiceId) {

  if (!primaryInvoiceId) {

    return false;
  }

  const primaryInvoice = await Service.getThirdPartyInvoice(tenantId, primaryInvoiceId);
  return !primaryInvoice.deleted && primaryInvoice?.customData?.feesTTC !== centToEuro(fees);
}

export function canUpdateFolder(folder) {

  return !folder.rollings?.updated && folder.status?.key !== FOLDER_STATUS_CANCELLED;
}

export async function useFormatFolder(tenantId, folder, dispute) {

  const formattedFolder = {
    ...await useFormatFolderLineHeader(tenantId, folder, dispute),
    ...await useFormatFolderLineContent(tenantId, folder),
    address                     : folder.address,
    booking_ref                 : folder.booking || '',
    buyerPartnerDelegation      : folder.buyerPartnerDelegation,
    buyerPartnerLead            : await formatPartnerLead(tenantId, folder.buyerPartnerLead),
    clientReimbursement         : centToEuro(folder.clientReimbursement),
    clientFollowUpUsers         : await getKeyValueArray(tenantId, folder.clientFollowUpUsers),
    comment                     : folder.comment || '',
    commissionsNote             : folder.commissionsNote,
    compromiseSignatureDeadline : folder.compromiseSignatureDeadline ? new Date(folder.compromiseSignatureDeadline) : '',
    compromiseSignatureDate     : folder.compromiseSignatureDate ? new Date(folder.compromiseSignatureDate) : '',
    compromiseSigned            : folder.compromiseSigned,
    diagnosticReimbursement     : {
      active : typeof folder.diagnosticReimbursement.active === 'boolean' ?
        folder.diagnosticReimbursement.active.toString() : '',
      value  : folder.diagnosticReimbursement.value ? centToEuro(folder.diagnosticReimbursement.value) : null
    },
    electronicCDV               : folder.electronicCDV,
    estimatedActSignatureDate   : folder.estimatedActSignatureDate ? new Date(folder.estimatedActSignatureDate) : '',
    feesHT                      : centToEuro(folder.fees / (1 + folder.vat)),
    feesPayableBy               : folder.feesPayableBy ? getKeyValueTrad('folders.feesPayableBy', folder.feesPayableBy) : '',
    furnituresAmount            : folder.furnituresAmount ? centToEuro(folder.furnituresAmount) : 0,
    history                     : folder.history,
    inconsistentFees            : await areFeesInconsistent(tenantId, folder.fees, folder.primaryInvoice),
    internalNote                : folder.internalNote || '',
    isCommissionsCalculated     : folder.calculated ?? await isCommissionsCalculated(tenantId, folder.id),
    lot_ref                     : folder.lot ? folder.lot : '',
    mandate                     : folder.mandate ? getIdReferenceObject(folder.mandate) : '',
    mandatePartnerLead          : await formatPartnerLead(tenantId, folder.mandatePartnerLead),
    offer_ref                   : folder.offer || '',
    owner                       : await getKeyValueProfile(tenantId, folder.owner),
    participants                : folder.participants,
    priceHAI                    : centToEuro(folder.price + folder.fees),
    primaryInvoice              : folder.primaryInvoice || '',
    primaryNotaryInvoice        : folder.primaryNotaryInvoice || '',
    saleType                    : folder.saleTypeId ? await getSaleType(tenantId, folder.saleTypeId) : '',
    securityDepositAmount       : folder.securityDepositAmount ? centToEuro(folder.securityDepositAmount) : 0,
    validationByHeadOffice      : getKeyValueTrad('folders.validationByHeadOffice', folder.validationByHeadOffice),
    vat                         : folder.vat,
    vefa                        : getIsVefaSaleType(folder.saleTypeId),
    vendorPartnerDelegation     : folder.vendorPartnerDelegation
  };

  formattedFolder.hasProformaUpdatedManually = await hasProformaUpdatedManually(tenantId, { ...folder, ...formattedFolder });

  return formattedFolder;
}

export function useUnformatFolder(newFolder, oldFolder) {

  const fieldsUpdated = {};

  for (const field of Object.keys(newFolder)) {

    switch (field) {
      case 'actSignatureDate' :
      case 'classeurId' :
      case 'comment' :
      case 'commissionsNote' :
      case 'compromiseSignatureDeadline' :
      case 'compromiseSignatureDate' :
      case 'electronicCDV':
      case 'compromiseSigned':
      case 'estimatedActSignatureDate' :
      case 'name' :
      case 'buyerPartnerDelegation' :
      case 'vendorPartnerDelegation' :
        if (JSON.stringify(newFolder[field]) !== JSON.stringify(oldFolder[field])) {
          fieldsUpdated[field] = newFolder[field] === '' ? null : newFolder[field];
        }

        break;

      case 'price':
        if (JSON.stringify(newFolder[field]) !== JSON.stringify(oldFolder[field])) {
          fieldsUpdated[field] = newFolder[field] === '' ? null : euroToCent(newFolder[field]);
        }

        break;

      case 'feesHT':
        if (JSON.stringify(newFolder[field]) !== JSON.stringify(oldFolder[field])) {
          fieldsUpdated['fees'] = newFolder[field] === '' ? null : Math.round(euroToCent(newFolder[field]) * (1 + newFolder.vat));
        }

        break;

      case 'fees':
        if (JSON.stringify(newFolder[field]) !== JSON.stringify(oldFolder[field])) {
          fieldsUpdated[field] = newFolder[field] === '' ? null : euroToCent(newFolder[field]);
        }

        break;

      case 'assignedTo':
      case 'cdvType' :
      case 'loanStatus' :
      case 'primaryNotary' :
        if (JSON.stringify(newFolder[field]) !== JSON.stringify(oldFolder[field])) {
          fieldsUpdated[field] = newFolder[field].key ? newFolder[field].key : null;
        }

        break;

      case 'feesPayableBy' :
      case 'status':
      case 'validationByHeadOffice' :
        if (JSON.stringify(newFolder[field]) !== JSON.stringify(oldFolder[field])) {
          fieldsUpdated[field] = newFolder[field].key;
        }

        break;

      case 'buyers' :
      case 'sellers':
        if (JSON.stringify(newFolder[field]) !== JSON.stringify(oldFolder[field])) {
          fieldsUpdated[field] = newFolder[field].map(client => {

            return {
              id            : client.key,
              notaryId      : client?.notary?.key,
              notaryClerkId : client?.clerk?.key
            };
          });
        }

        break;

      case 'diagnosticReimbursement' :
        if (JSON.stringify(newFolder[field]) !== JSON.stringify(oldFolder[field])) {
          if (newFolder[field].active === 'true') {
            fieldsUpdated[field] = {
              active : true,
              value  : euroToCent(newFolder[field].value)
            };
          } else {
            fieldsUpdated[field] = {
              active : false
            };
          }
        }

        break;

      case 'clientReimbursement' :
      case 'furnituresAmount' :
      case 'securityDepositAmount' :
        if (JSON.stringify(newFolder[field]) !== JSON.stringify(oldFolder[field])) {
          fieldsUpdated[field] = euroToCent(newFolder[field]);
        }

        break;

      case 'buyerPartnerLead' :
      case 'mandatePartnerLead' :
        if (JSON.stringify(newFolder[field]) !== JSON.stringify(oldFolder[field])) {
          const partnerLead = {};

          for (const key of Object.keys(newFolder[field])) {

            if (key === 'source' && newFolder[field].source.type.key && newFolder[field].source.value.key) {

              // Add type and id if both are filled
              partnerLead.source = {
                type : newFolder[field].source.type.key,
                id   : newFolder[field].source.value.key
              };
            } else if (key === 'source' && newFolder[field].source.type.key) {

              // Add only type if value is not filled
              partnerLead.source = {
                type : newFolder[field].source.type.key
              };
            } else if (key === 'amount' && newFolder[field].source.value.key && newFolder[field][key] !== null) {

              // Add amount if value are set
              partnerLead.amount = {
                percent : newFolder[field].amount.percent,
                value   : newFolder[field].amount.percent ? newFolder[field].amount.value : euroToCent(newFolder[field].amount.value)
              };
            }
          }

          fieldsUpdated[field] = partnerLead;
        }

        break;

      case 'clientFollowUpUsers' :
        if (JSON.stringify(newFolder[field]) !== JSON.stringify(oldFolder[field])) {
          fieldsUpdated[field] = newFolder[field].map(value => value.key);
        }

        break;
    }
  }

  return fieldsUpdated;
}

//TODO: Retirer posthog de cette partie a la fin des features flags
export async function useFolderTabs(tenant, folder, posthog) {

  const tabs = [{ name : 'information' }, { name : 'notaries' }];

  tabs.push({ name : 'charges-partners' });

  tabs.push({ name : 'tasks' });

  if (PermissionManager.getPermissionForElement(PermissionManager.permissionElement.dossiers.documents.show(folder))) {
    tabs.push({ name : 'documents' });
  }

  tabs.push({ name : 'commissions' });

  tabs.push({ name : 'invoices' }, { name : 'alerts', number : await getFolderAlertsNumber(tenant, folder) });

  if (PermissionManager.getPermissionForElement(PermissionManager.permissionElement.dossiers.sandbox.show)) {
    tabs.push({ name : 'sandbox' });
  }

  return tabs;
}

export async function getFolderAlertsNumber(tenant, folder) {

  if (!folder && !folder.id) {

    return null;
  }

  try {

    let alertsNumber = 0;
    const alerts = await useGetFolderAlerts(tenant, folder);
    alertsNumber += alerts.length;
    const { commissions } = await useGetCommissionData(tenant, folder);
    alertsNumber += useGetTotalCommission(commissions) > useGetCalculationBasis(folder) ? 1 : 0;

    if (folder.inconsistentFees) {
      alertsNumber++;
    }

    return alertsNumber;
  } catch {

    return null;
  }
}

export async function useGetFolderAlerts(tenantId, folder) {

  const alerts = [];

  if (!folder.mandate?.id || !folder.mandate?.id.startsWith('MAN')) {

    return alerts;
  }

  try {

    const mandate = await Service.getMandate(tenantId, folder.mandate.id);
    const expiresInLessThan3Months = useIsMandateExpireInLessThan3Months(mandate.expiryDate);
    if (expiresInLessThan3Months) {

      const messages = i18n.global.messages[i18n.global.locale];
      const text = messages.folders.create.messages.mandateExpiresOn.replace('{date}', expiresInLessThan3Months);
      alerts.push({
        type    : 'warning',
        message : text
      });
    }

    return alerts;
  } catch (e) {

    throw new Error(e);
  }
}

/**
 * Get message if the signature date is too far or too old.
 * Do not consider the folders for which the commissions have already been calculated.
 */
export function isSignatureDateTooFar(folder) {

  if (folder.rollings.updated) {

    return false;
  }

  if (dayjs(folder.actSignatureDate).diff(dayjs(), 'month') > 6) {

    return i18n.global.messages[i18n.global.locale].folders.details.alerts.signatureDateTooFar;
  } else if (dayjs().diff(folder.actSignatureDate, 'month') > 6) {

    return i18n.global.messages[i18n.global.locale].folders.details.alerts.signatureDateTooOld;
  }

  return undefined;
}
